/**
 * Service Analytics unifié (web) — point d'entrée unique pour
 * PostHog + Amplitude (product analytics) + Google Analytics 4 + Meta Pixel
 * (attribution).
 *
 * Calqué sur `services/analytics/index.ts` de l'app mobile pour garder la même
 * API d'un côté à l'autre. Côté web on a 4 destinations ; Sentry / TikTok
 * pourront s'ajouter ici.
 *
 * ── Qui reçoit quoi ────────────────────────────────────────────────────────
 *
 * | Destination | Reçoit                          | Pourquoi                  |
 * |-------------|---------------------------------|---------------------------|
 * | Google      | tout                            | attribution, non facturé  |
 * | Meta        | tout                            | attribution, non facturé  |
 * | Amplitude   | tout le plan + les vues de page | 2 M/mois, large marge     |
 * | PostHog     | POSTHOG_EVENTS uniquement       | 1 M/mois PARTAGÉ avec le mobile, et la collecte S'ARRÊTE au dépassement |
 *
 * Les événements produit sont filtrés par le plan (`./tracking-plan`) ; Google
 * et Meta, eux, reçoivent tout puisqu'ils ne facturent pas à l'événement.
 *
 * Utilisation :
 *   import { analytics } from "@/services/analytics";
 *   analytics.identifyUser(user);
 *   analytics.trackEvent("order_placed", { amount: 1200 });
 *   analytics.trackPurchase(1200, "DZD", { transaction_id, items });
 *   analytics.trackPage("/home");
 *
 * Ciblage des destinations (optionnel) :
 *   analytics.trackEvent("scroll_feed", props, { except: ["posthog"] });
 *   analytics.trackEvent("ad_impression", props, { to: ["google", "facebook"] });
 */
import {
  posthog,
  POSTHOG_ENABLED,
  identifyPostHogUser,
  posthogReset,
} from "./posthog";
import {
  logGoogleEvent,
  logGooglePageView,
  logGooglePurchase,
  setGoogleUserId,
  type GA4Item,
} from "./google";
import {
  logFacebookEvent,
  logFacebookPageView,
  logFacebookPurchase,
  setFacebookUserData,
  resetFacebookUser,
} from "./facebook";
import {
  identifyAmplitudeUser,
  amplitudeReset,
  trackAmplitudeEvent,
  trackAmplitudePurchase,
  setAmplitudeUserProperties,
} from "./amplitude";
import { POSTHOG_EVENTS, isInPlan, toAmplitudeEvent } from "./tracking-plan";

interface UserInfo {
  id?: string;
  phone?: string;
  email?: string;
  name?: string;
}

/** Destinations possibles pour un événement analytics. */
export type AnalyticsDestination =
  | "posthog"
  | "amplitude"
  | "google"
  | "facebook";

const ALL_DESTINATIONS: readonly AnalyticsDestination[] = [
  "posthog",
  "amplitude",
  "google",
  "facebook",
];

/**
 * Options de ciblage d'un événement.
 * - `to`     → liste blanche : n'envoyer qu'à ces destinations.
 * - `except` → liste noire : envoyer partout SAUF à ces destinations.
 * - aucune   → toutes les destinations (comportement par défaut).
 * `to` est prioritaire sur `except`.
 */
export interface TrackOptions {
  to?: AnalyticsDestination[];
  except?: AnalyticsDestination[];
}

function resolveDestinations(
  options?: TrackOptions
): Set<AnalyticsDestination> {
  if (options?.to) return new Set(options.to);
  if (options?.except) {
    const excluded = new Set(options.except);
    return new Set(ALL_DESTINATIONS.filter((d) => !excluded.has(d)));
  }
  return new Set(ALL_DESTINATIONS);
}

export const analytics = {
  /** Identifier l'utilisateur dans tous les services (login/signup). */
  identifyUser(user: UserInfo) {
    identifyPostHogUser(user);
    identifyAmplitudeUser(user);
    setGoogleUserId(user.id ?? null);
    setFacebookUserData(user);
  },

  /** Logout — nettoyer l'identité dans tous les services. */
  logout() {
    posthogReset();
    amplitudeReset();
    setGoogleUserId(null);
    resetFacebookUser();
  },

  /**
   * Tracker un événement métier.
   *
   * Google et Meta reçoivent tout. Le product analytics est filtré par le plan
   * (cf. `./tracking-plan`) : PostHog ne reçoit que `POSTHOG_EVENTS`, Amplitude
   * tout le plan, sous son nom Object-Action. Un événement hors plan n'atteint
   * ni l'un ni l'autre et avertit en dev.
   */
  trackEvent(
    event: string,
    properties?: Record<string, unknown>,
    options?: TrackOptions
  ) {
    const to = resolveDestinations(options);
    const inPlan = isInPlan(event);

    if (inPlan && to.has("posthog") && POSTHOG_ENABLED && POSTHOG_EVENTS.has(event))
      posthog.capture(event, properties);
    if (inPlan && to.has("amplitude")) {
      const amplitudeName = toAmplitudeEvent(event);
      if (amplitudeName) trackAmplitudeEvent(amplitudeName, properties);
    }
    if (to.has("google")) logGoogleEvent(event, properties);
    if (to.has("facebook")) logFacebookEvent(event, properties);
  },

  /** Tracker un achat → PostHog `purchase` + Google `purchase` + Meta `Purchase`. */
  trackPurchase(
    amount: number,
    currency: string,
    properties?: Record<string, unknown> & {
      transaction_id?: string;
      order_id?: string;
      items?: GA4Item[];
    },
    options?: TrackOptions
  ) {
    const to = resolveDestinations(options);
    // Volontairement HORS liste blanche : les achats sont rares et portent le
    // revenu — les filtrer économiserait quelques milliers d'événements en
    // sacrifiant la seule métrique monétaire.
    if (to.has("posthog") && POSTHOG_ENABLED)
      posthog.capture("purchase", { amount, currency, ...properties });
    if (to.has("amplitude"))
      trackAmplitudePurchase(amount, currency, properties);
    if (to.has("google"))
      logGooglePurchase({
        transaction_id:
          properties?.transaction_id ??
          properties?.order_id ??
          `txn_${Date.now()}`,
        currency,
        value: amount,
        items: properties?.items ?? [],
        ...properties,
      });
    if (to.has("facebook"))
      logFacebookPurchase({
        currency,
        value: amount,
        items: properties?.items ?? [],
        // Dédup navigateur ↔ Conversions API si elle est branchée plus tard.
        eventID: properties?.transaction_id ?? properties?.order_id,
      });
  },

  /**
   * Tracker un changement de page (SPA) → PostHog `$pageview` + Google
   * `page_view` + Meta `PageView`.
   *
   * **Pas Amplitude** : son autocapture `pageViews` suit déjà l'historique de
   * navigation, l'appeler ici compterait chaque vue deux fois.
   */
  trackPage(
    path: string,
    properties?: Record<string, unknown>,
    options?: TrackOptions
  ) {
    const to = resolveDestinations(options);
    if (to.has("posthog") && POSTHOG_ENABLED)
      posthog.capture("$pageview", properties);
    if (to.has("google"))
      logGooglePageView(path, properties?.title as string | undefined);
    if (to.has("facebook")) logFacebookPageView();
  },

  /**
   * Propriétés persistantes attachées aux événements suivants.
   *
   * PostHog les stocke comme super properties (jointes à CHAQUE événement) ;
   * Amplitude n'a pas d'équivalent exact et les enregistre comme propriétés
   * UTILISATEUR — même intention, granularité différente. C'est le moyen de
   * segmenter tout un parcours pour ZÉRO événement facturé.
   */
  registerSuperProperties(properties: Record<string, unknown>) {
    if (POSTHOG_ENABLED) posthog.register(properties);
    setAmplitudeUserProperties(properties);
  },

  /**
   * Capturer une erreur. Pas de Sentry côté web pour l'instant : on log en
   * console et on remonte l'exception dans PostHog (error tracking) si actif.
   */
  captureError(error: unknown, context?: Record<string, unknown>) {
    console.error("[analytics] captureError", error, context);
    if (POSTHOG_ENABLED) {
      posthog.captureException(
        error,
        context as Record<string, unknown> | undefined
      );
    }
  },
};
